import { useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import SolidPlaneDepartureIcon from 'LuxKit/Icons/solid/SolidPlaneDepartureIcon'
import TextLink from 'LuxKit/TextLink'
import Colour from 'components/utils/Colour'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LuxPlusExperienceDiscountTakeoverBackground from 'takeovers/luxplusExperienceDiscountTakeover/components/LuxPlusExperienceDiscountTakeoverBackground'
import useLuxPlusFlightVoucherBanner from 'luxPlus/hooks/useLuxPlusFlightVoucherBanner'
import { LUX_PLUS_FLIGHT_VOUCHER_CAMPAIGN } from 'luxPlus/constants/base'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import styled from 'styled-components'
import type { PageBannerCarouselItem } from 'LuxKit/Banners/types'

const Background = styled(LuxPlusExperienceDiscountTakeoverBackground)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`

function useLuxPlusFlightVoucherOfferBannerItem(): PageBannerCarouselItem | null {
  const {
    showBanner,
    onDismiss,
    ctaLabel,
    ctaTo,
    ctaHref,
    ctaOnClick,
  } = useLuxPlusFlightVoucherBanner()

  return useMemo(() => {
    if (!showBanner) return null

    return {
      id: 'luxplus-flight-voucher-offer',
      kind: 'lux-plus',
      icon: <SolidPlaneDepartureIcon size="M" />,
      onDismiss,
      customBackground: <Background />,
      actionButtonProps: {
        kind: 'secondary',
        children: ctaLabel,
        to: ctaTo,
        href: ctaHref,
        onClick: ctaOnClick,
      },
      hideMobileActionButton: true,
      children: <>
        <CSSBreakpoint min="desktop">
          <FormattedMessage
            id="wIXQfnMWjUvIXn_5s9D6"
            defaultMessage="<luxPlusLabel>LuxPlus<plus>+</plus></luxPlusLabel> Exclusive! SAVE up to $800pp on international return flights. Ends {endDate}. T&Cs apply."
            description="LuxPlus+ flight voucher promotional banner message"
            values={{
              luxPlusLabel: chunks => <b>{chunks}</b>,
              plus: chunks => <Colour value="highlight-lux-plus">{chunks}</Colour>,
              endDate: <FormatDateTime value={LUX_PLUS_FLIGHT_VOUCHER_CAMPAIGN.END_DATE} day="numeric" month="short" year="numeric" />,
            }}
          />
        </CSSBreakpoint>
        <CSSBreakpoint max="tablet">
          <FormattedMessage
            id="FCmfXzXyEipVf3hZ21qZ"
            defaultMessage="<luxPlusLabel>LuxPlus<plus>+</plus></luxPlusLabel> Exclusive! SAVE up to $800pp on international return flights. Ends {endDate}. T&Cs apply. <ctaLink>{ctaLabel}</ctaLink>"
            description="LuxPlus flight voucher promotional banner message with inline call to action link for mobile"
            values={{
              luxPlusLabel: chunks => <b>{chunks}</b>,
              plus: chunks => <Colour value="highlight-lux-plus">{chunks}</Colour>,
              endDate: <FormatDateTime value={LUX_PLUS_FLIGHT_VOUCHER_CAMPAIGN.END_DATE} day="numeric" month="short" year="numeric" />,
              ctaLabel,
              ctaLink: chunks => (
                <TextLink
                  key="flight-voucher-mobile-cta-link"
                  to={ctaTo}
                  href={ctaHref}
                  onClick={ctaOnClick}
                  size="caption"
                >
                  {chunks}
                </TextLink>
              ),
            }}
          />
        </CSSBreakpoint>
      </>,
    }
  }, [showBanner, onDismiss, ctaLabel, ctaTo, ctaHref, ctaOnClick])
}

export default useLuxPlusFlightVoucherOfferBannerItem
